// deps
import React, { ReactElement, memo, useCallback, useMemo } from "react";

// interfaces
import { UpiPaymentMethod, IUpiPaymentGroup, PaymentMethodProps } from "@payment-ui/interfaces";

// widgets
import { PaymentMethodListView as UpiListView } from "@payment-ui/widgets/payment-method-views/list-view";
import { PaymentMethodIconView as UpiIconView } from "@payment-ui/widgets/payment-method-views/icon-view";

// helpers
import { canEnablePaymentMethod, getPaymentMethodDisplayName } from "@payment-ui/helpers/Payment";
import { getPayCtaMsg, isUpiIntentMethod } from "@payment-ui/helpers/Upi";

// test-ids
import { UPI_PAYMENT_METHOD_TEST_IDS, UPI_INTENT_PAYMENT_METHOD_TEST_IDS } from "@swiggy-private/payment-test-ids";

interface UpiProps extends PaymentMethodProps {
    methodData: UpiPaymentMethod;
    paymentActions: IUpiPaymentGroup["paymentActions"];
    paymentAmount: string;
    isIconView?: boolean;
    paymentAmountWithoutDiscount?: string;
    onClickPaymentGroups?: (context: string, label: string, value: string) => void;
    position?: number;
    isThirdPartyIntentApp?: boolean;
}

function UPIPaymentMethod(props: UpiProps): ReactElement {
    const {
        methodData,
        paymentActions,
        isPaymentGroupEnabled,
        selected,
        isIconView,
        paymentAmount,
        onClickPaymentGroups,
        position,
        isThirdPartyIntentApp,
    } = props;

    const isMethodEnabled = useMemo(() => canEnablePaymentMethod(methodData, isPaymentGroupEnabled), [
        isPaymentGroupEnabled,
        methodData,
    ]);
    const isIntentMethod = useMemo(() => isUpiIntentMethod(methodData), [methodData]);
    const testIds = useMemo(() => (isIntentMethod ? UPI_INTENT_PAYMENT_METHOD_TEST_IDS : UPI_PAYMENT_METHOD_TEST_IDS), [
        isIntentMethod,
    ]);
    const payCtaMsg = useMemo(() => getPayCtaMsg(methodData, paymentAmount), [methodData, paymentAmount]);

    const onPay = useCallback(
        (extraData = {}): void => {
            if (!isMethodEnabled) {
                return;
            }
            paymentActions.onPay(props.methodData, {}, extraData);
        },
        [isMethodEnabled, paymentActions, props.methodData],
    );

    const onClick = useCallback((): void => {
        onClickPaymentGroups &&
            onClickPaymentGroups(
                getPaymentMethodDisplayName(props.methodData),
                String(isMethodEnabled),
                String(position),
            );

        if (!isMethodEnabled) {
            return;
        }
        paymentActions.onSelect(props.methodData);
        if (isIconView) {
            onPay();
        }
    }, [isIconView, isMethodEnabled, onPay, paymentActions, props.methodData, onClickPaymentGroups, position]);

    return !isIconView ? (
        <UpiListView
            methodData={props.methodData}
            isMethodEnabled={isMethodEnabled}
            onClick={onClick}
            onPayButtonClick={onPay}
            testIds={testIds}
            selected={selected}
            payCtaMsg={payCtaMsg}
            isThirdPartyIntentApp={isThirdPartyIntentApp}
        />
    ) : (
        <UpiIconView
            isMethodEnabled={isMethodEnabled}
            methodData={props.methodData}
            testIds={testIds}
            onClick={onClick}
        />
    );
}

export default memo(UPIPaymentMethod);
